<template>
  <vs-dialog v-model="isOpen" width="640px" prevent-close @close="close">
    <template #header>
      <h4 class="dialog-title">
        <i class="fal fa-people-carry"></i> ຍ້າຍກະເປົ໋າສາຂາ (ປິດສາຂາ)
      </h4>
    </template>

    <div class="dialog-content">
      <!-- RESULT panel: shown after a completed (or replayed) migration, instead of the form. -->
      <div v-if="result" class="migrate-result">
        <div class="notification" :class="result.is_duplicate ? 'is-warning' : 'is-success'">
          <p v-if="result.is_duplicate"><strong>ລາຍການນີ້ຖືກດຳເນີນການແລ້ວ</strong> — ສະແດງຜົນເດີມ</p>
          <p v-else-if="result.finalized"><strong>ປິດກະເປົ໋າຕົ້ນທາງແລ້ວ</strong> (ບໍ່ມີຍອດໃຫ້ຍ້າຍ)</p>
          <p v-else><strong>ຍ້າຍກະເປົ໋າສຳເລັດ</strong></p>
          <p class="result-branches">
            {{ branchText(sourceBranch, result.source_branch_id) }}
            <i class="fal fa-long-arrow-right"></i>
            {{ branchText(destBranch, result.dest_branch_id) }}
          </p>
        </div>
        <table class="table is-fullwidth is-narrow result-table">
          <thead>
            <tr><th>ກະເປົ໋າ</th><th>ຜົນ</th><th class="has-text-right">ຕົ້ນທາງ ຫຼັງຍ້າຍ</th><th class="has-text-right">ປາຍທາງ ຫຼັງຍ້າຍ</th></tr>
          </thead>
          <tbody>
            <tr v-for="t in WALLET_TYPES" :key="t.value">
              <td>{{ t.label }}</td>
              <template v-if="resultFor(t.value)">
                <td>
                  <span :class="resultFor(t.value).direction === 'DEBT' ? 'has-text-danger' : 'has-text-success'">
                    {{ resultFor(t.value).direction === 'DEBT' ? 'ຍ້າຍໜີ້' : 'ຍ້າຍເງິນ' }}
                    {{ formatMoney(resultFor(t.value).signed_amount) }}
                  </span>
                </td>
                <td class="has-text-right">{{ formatMoney(resultFor(t.value).source_balance_after) }}</td>
                <td class="has-text-right">{{ formatMoney(resultFor(t.value).dest_balance_after) }}</td>
              </template>
              <template v-else>
                <td colspan="3" class="has-text-grey">ບໍ່ມີຍອດໃຫ້ຍ້າຍ (0.00)</td>
              </template>
            </tr>
          </tbody>
        </table>
        <p v-if="result.source_deactivated" class="help-text">
          <i class="fal fa-lock"></i> ກະເປົ໋າຕົ້ນທາງທັງສອງຖືກປິດແລ້ວ (is_active = false)
        </p>
      </div>

      <template v-else>
        <div class="notification is-warning is-light migrate-warning">
          <p><strong>ຍ້າຍຍອດທັງໝົດ</strong> ຂອງສາຂາຕົ້ນທາງ (ທັງ ຄ່າຂົນສົ່ງ ແລະ COD, ທັງເງິນ ແລະ ໜີ້) ໄປສາຂາປາຍທາງ
            ໃນລາຍການດຽວ. <strong>ບໍ່ສາມາດຍົກເລີກໄດ້</strong> — ກວດສອບຍອດທີ່ສະແດງກ່ອນຢືນຢັນ.</p>
        </div>

        <div class="columns is-multiline">
          <div class="column is-6">
            <div class="form-group">
              <label class="label">ສາຂາຕົ້ນທາງ (ທີ່ປິດ) <span class="required">*</span></label>
              <BranchSelect
                  v-model="form.source_branch_id"
                  :allow-all="false"
                  include-closed
                  disabled-first
                  :disabled="isSubmitting"
                  placeholder="ເລືອກສາຂາທີ່ປິດ..."
                  @select="sourceBranch = $event"
              />
            </div>
          </div>
          <div class="column is-6">
            <div class="form-group">
              <label class="label">ສາຂາປາຍທາງ (ຮັບໂອນ) <span class="required">*</span></label>
              <BranchSelect
                  v-model="form.dest_branch_id"
                  :allow-all="false"
                  :disabled="isSubmitting"
                  placeholder="ເລືອກສາຂາຮັບໂອນ..."
                  @select="destBranch = $event"
              />
              <p v-if="isSameBranch" class="help-text help-error">ສາຂາຕົ້ນທາງ ແລະ ປາຍທາງ ຕ້ອງບໍ່ຊ້ຳກັນ</p>
              <p v-else-if="sourceStillEnabled" class="help-text help-error">
                ສາຂາຕົ້ນທາງຍັງເປີດໃຊ້ກະເປົ໋າຢູ່ (wallet_enabled) — ຕ້ອງປິດກ່ອນ (ຂັ້ນຕອນ 0 ຂອງຄູ່ມື) ບໍ່ດັ່ງນັ້ນສາຂາຈະຖືກຄິດເງິນຕໍ່ໃສ່ກະເປົ໋າທີ່ຖືກຍ້າຍອອກແລ້ວ
              </p>
            </div>
          </div>
        </div>

        <!-- Live position of the SOURCE — the values Go must still see under the lock. -->
        <div v-if="form.source_branch_id" class="wallet-panel">
          <div class="panel-heading-row">
            <span class="panel-title">ຍອດກະເປົ໋າຕົ້ນທາງ (ສົດ)</span>
            <button type="button" class="button is-small is-white refresh-btn" :class="{ 'is-loading': source.loading }"
                    :disabled="isSubmitting" title="ໂຫລດຍອດໃໝ່" @click="fetchSourceWallets">
              <i class="fal fa-sync-alt"></i>
            </button>
          </div>
          <p v-if="source.error" class="help-text help-error">{{ source.error }}</p>
          <table v-else-if="source.wallets" class="table is-fullwidth is-narrow wallet-table">
            <thead>
              <tr><th>ກະເປົ໋າ</th><th class="has-text-right">ຍອດເງິນ</th><th>ສະຖານະຍອດ</th><th class="has-text-right">ຖືກຈອງ</th><th>ກະເປົ໋າ</th></tr>
            </thead>
            <tbody>
              <tr v-for="t in WALLET_TYPES" :key="t.value">
                <td>{{ t.label }}</td>
                <td class="has-text-right"><strong>{{ formatMoney(sourceWallet(t.value).balance) }}</strong></td>
                <td>
                  <span class="tag" :class="positionClass(sourceWallet(t.value).balance)">
                    {{ positionLabel(sourceWallet(t.value).balance) }}
                  </span>
                </td>
                <td class="has-text-right" :class="{ 'has-text-danger': isHeld(sourceWallet(t.value)) }">
                  {{ formatMoney(sourceWallet(t.value).reserved_amount) }}
                </td>
                <td>
                  <span class="tag" :class="sourceWallet(t.value).is_active ? 'is-success is-light' : 'is-dark'">
                    {{ sourceWallet(t.value).is_active ? 'ເປີດ' : 'ປິດ' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-else-if="source.loading" class="help-text">ກຳລັງໂຫລດ...</p>
          <p v-if="hasHold" class="help-text help-error">
            <i class="fal fa-exclamation-triangle"></i>
            ມີຍອດຖືກຈອງຄ້າງຢູ່ — ບໍ່ສາມາດຍ້າຍໄດ້. ລໍຖ້າໃຫ້ລາຍການຈອງໝົດອາຍຸ (ລະບົບລ້າງທຸກ 10 ນາທີ) ແລ້ວກົດໂຫລດຍອດໃໝ່.
          </p>
          <p v-if="bothZero && !form.deactivate_source" class="help-text">
            ຍອດທັງສອງເປັນ 0.00 — ບໍ່ມີຍອດໃຫ້ຍ້າຍ. <strong>ຖ້າລາຍການຄ້າງຂອງສາຂາເກົ່າໝົດແລ້ວ (ຄັ້ງສຸດທ້າຍ)</strong>
            ຕິກ "ປິດກະເປົ໋າຕົ້ນທາງ" ເພື່ອປິດກະເປົ໋າ; ຖ້າຍັງບໍ່ໝົດ ປິດໜ້າຕ່າງນີ້ — ບໍ່ຕ້ອງເຮັດຫຍັງຕອນນີ້.
          </p>
        </div>

        <!-- Destination: display only, so the operator sees where the position lands and whether it can. -->
        <div v-if="form.dest_branch_id && !isSameBranch" class="wallet-panel">
          <div class="panel-heading-row">
            <span class="panel-title">ຍອດກະເປົ໋າປາຍທາງ (ສົດ)</span>
          </div>
          <p v-if="dest.error" class="help-text help-error">{{ dest.error }}</p>
          <table v-else-if="dest.wallets" class="table is-fullwidth is-narrow wallet-table">
            <tbody>
              <tr v-for="t in WALLET_TYPES" :key="t.value">
                <td>{{ t.label }}</td>
                <td class="has-text-right">{{ formatMoney(destWallet(t.value).balance) }}</td>
                <td>
                  <span class="tag" :class="destWallet(t.value).is_active ? 'is-success is-light' : 'is-danger'">
                    {{ destWallet(t.value).is_active ? 'ເປີດ' : 'ປິດ' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-if="destInactive" class="help-text help-error">ກະເປົ໋າປາຍທາງຖືກປິດ — ຕ້ອງເປີດກ່ອນຈຶ່ງຮັບໂອນໄດ້</p>
        </div>

        <div class="form-group">
          <label class="checkbox deactivate-check">
            <input type="checkbox" v-model="form.deactivate_source" :disabled="isSubmitting">
            ປິດກະເປົ໋າຕົ້ນທາງດ້ວຍ (is_active = false)
          </label>
          <p class="help-text">
            ປົກກະຕິ <strong>ບໍ່ຕິກ</strong> ໃນຄັ້ງທຳອິດ: ຫຼັງຍ້າຍແລ້ວ ສາຂາເກົ່າຍັງມີລາຍການຄ້າງ (ຄືນເງິນ, ປັບປຸງລາຄາ)
            ທີ່ຕ້ອງລົງກະເປົ໋າເກົ່າ. ຕິກສະເພາະຄັ້ງສຸດທ້າຍ ເມື່ອລາຍການຄ້າງໝົດແລ້ວ — ຍ້າຍຍອດທີ່ເຫຼືອ ແລະ ປິດກະເປົ໋າ.
          </p>
        </div>

        <div class="form-group">
          <label class="label">ລາຍລະອຽດ / ເຫດຜົນ <span class="required">*</span></label>
          <textarea
              class="textarea"
              v-model="form.remark"
              rows="2"
              maxlength="500"
              :disabled="isSubmitting"
              placeholder="ເຫດຜົນ (ເຊັ່ນ: ປິດສາຂາ 30/09/2026, ຍ້າຍໄປສາຂາ ...)"
          ></textarea>
        </div>

        <div class="form-group">
          <label class="label">ເອກະສານປະກອບ (ບໍ່ບັງຄັບ)</label>
          <div class="file-upload">
            <input type="file" ref="fileInput" accept="image/*,.pdf" @change="handleFileSelect" style="display: none">
            <button type="button" class="button is-small" :disabled="isSubmitting" @click="$refs.fileInput.click()">
              <i class="fal fa-upload"></i>&nbsp;ເລືອກໄຟລ໌
            </button>
            <span v-if="selectedFile" class="file-name">
              {{ selectedFile.name }}
              <i v-if="!isSubmitting" class="fal fa-times" @click="clearFile" style="cursor: pointer; margin-left: 8px;"></i>
            </span>
          </div>
          <p class="help-text">ຮອງຮັບ: JPG, PNG, PDF (ສູງສຸດ 5MB)</p>
        </div>

        <!-- Explicit confirm: restate OLD → NEW and the signed amounts before the money moves. -->
        <div v-if="showConfirm" class="notification is-warning migrate-confirm">
          <p><strong>{{ bothZero ? 'ກວດສອບການປິດກະເປົ໋າ' : 'ກວດສອບການຍ້າຍກະເປົ໋າ' }}</strong> — ບໍ່ສາມາດຍົກເລີກໄດ້</p>
          <p>ຈາກ: <strong>{{ branchText(sourceBranch, form.source_branch_id) }}</strong>
            → ໄປ: <strong>{{ branchText(destBranch, form.dest_branch_id) }}</strong></p>
          <p v-for="t in WALLET_TYPES" :key="t.value">
            {{ t.label }}:
            <strong v-if="isZero(sourceWallet(t.value).balance)">ບໍ່ມີຍອດໃຫ້ຍ້າຍ</strong>
            <strong v-else>{{ positionLabel(sourceWallet(t.value).balance) }} {{ formatMoney(sourceWallet(t.value).balance) }}</strong>
          </p>
          <p v-if="form.deactivate_source"><strong>ປິດກະເປົ໋າຕົ້ນທາງທັງສອງ</strong></p>
        </div>

        <div v-if="submitError" class="notification is-warning submit-error">
          <p>{{ submitError }}</p>
          <p v-if="submitErrorRequestId" class="error-ref">ref: {{ submitErrorRequestId }}</p>
        </div>
      </template>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <template v-if="result">
          <button type="button" class="button" @click="finish">ປິດ</button>
        </template>
        <template v-else-if="!showConfirm">
          <button type="button" class="button is-light" :disabled="isSubmitting" @click="close">ຍົກເລີກ</button>
          <button type="button" class="button migrate-btn" :class="{ 'is-danger': bothZero }"
                  :disabled="!canSubmit" @click="requestConfirm">
            <i class="fal fa-check"></i>&nbsp;{{ submitLabel }}
          </button>
        </template>
        <template v-else>
          <button type="button" class="button is-light" :disabled="isSubmitting" @click="showConfirm = false">ກັບຄືນ</button>
          <button type="button" class="button migrate-btn" :class="{ 'is-loading': isSubmitting, 'is-danger': bothZero }"
                  :disabled="isSubmitting" @click="submit">
            <i class="fal fa-check"></i>&nbsp;ຢືນຢັນ{{ submitLabel }}
          </button>
        </template>
      </div>
    </template>
  </vs-dialog>
</template>

<script>
import walletService, { extractErrorInfo } from '@/services/walletService';
import BranchSelect from '@/components/BranchSelect.vue';

const WALLET_TYPES = [
  { value: 'FREIGHT', label: 'ຄ່າຂົນສົ່ງ (FREIGHT)' },
  { value: 'COD', label: 'ເກັບເງິນປາຍທາງ (COD)' },
];

// Two-decimal money formatter. The dialog DISPLAYS exactly the value it SENDS as the expected
// balance (Go compares to the cent), so this never rounds to whole kip like walletFormatters does.
const moneyFormatter = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const isZeroAmount = (v) => Math.abs(Number(v) || 0) < 0.005;
const toExpected = (v) => (Number(v) || 0).toFixed(2);

export default {
  name: 'MigrateDialog',
  components: { BranchSelect },
  data() {
    return {
      WALLET_TYPES,
      isOpen: true,
      isSubmitting: false,
      submitError: null,
      submitErrorRequestId: null,
      // Stable idempotency key for THIS migration intent, reused across resubmits (a human retry after
      // a 60s upload timeout must send the SAME key or Go cannot dedup — the position would be moved
      // TWICE only if a residual landed meanwhile, but the second run would still be a second audit
      // row). Rotated whenever the INTENT changes: source, destination, the displayed balances (i.e.
      // the expected values), deactivate_source, slip. NOT on remark (mirrors Transfer.vue).
      idempotencyKey: `migrate-${crypto.randomUUID()}`,
      showConfirm: false,
      result: null,
      form: {
        source_branch_id: '',
        dest_branch_id: '',
        deactivate_source: false,
        remark: '',
      },
      sourceBranch: null,
      destBranch: null,
      // Live wallets of each side: { freight, cod } (Go WalletResp) or null while unloaded.
      source: { loading: false, error: null, wallets: null, seq: 0 },
      // The expected pair the current idempotency key was minted against ("F|C" two-decimal strings).
      // Survives a failed re-read (wallets → null) so the next good read can still detect a move.
      keyedExpected: null,
      dest: { loading: false, error: null, wallets: null, seq: 0 },
      selectedFile: null,
    };
  },
  computed: {
    isSameBranch() {
      return !!this.form.source_branch_id && !!this.form.dest_branch_id &&
          String(this.form.source_branch_id) === String(this.form.dest_branch_id);
    },
    // Runbook step 0 (SQL wallet_enabled = 0) must precede a migration: a still-enabled branch keeps
    // charging into a wallet that was just zeroed. The picker row carries the Laravel flag.
    sourceStillEnabled() {
      const b = this.sourceBranch;
      return !!b && !(b.wallet_enabled === false || b.wallet_enabled === 0 || b.wallet_enabled === '0');
    },
    sourceLoaded() {
      return !!(this.source.wallets && this.source.wallets.freight && this.source.wallets.cod);
    },
    hasHold() {
      if (!this.sourceLoaded) return false;
      return this.isHeld(this.source.wallets.freight) || this.isHeld(this.source.wallets.cod);
    },
    bothZero() {
      if (!this.sourceLoaded) return false;
      return isZeroAmount(this.source.wallets.freight.balance) && isZeroAmount(this.source.wallets.cod.balance);
    },
    destInactive() {
      const w = this.dest.wallets;
      if (!w || !w.freight || !w.cod) return false;
      return !w.freight.is_active || !w.cod.is_active;
    },
    submitLabel() {
      return this.bothZero ? 'ປິດກະເປົ໋າ' : 'ຍ້າຍກະເປົ໋າ';
    },
    canSubmit() {
      return (
          !this.isSubmitting &&
          !!this.form.source_branch_id &&
          !!this.form.dest_branch_id &&
          !this.isSameBranch &&
          !this.sourceStillEnabled &&
          this.sourceLoaded &&
          !this.source.loading &&
          !this.source.error &&
          !this.hasHold &&
          !this.destInactive &&
          // Both 0.00 is a 409 unless the operator asks to close (finalize mode, design D6).
          !(this.bothZero && !this.form.deactivate_source) &&
          this.form.remark.trim().length > 0 &&
          this.form.remark.length <= 500
      );
    },
  },
  watch: {
    'form.source_branch_id'(id) {
      this.rotateIdempotencyKey();
      this.source = { loading: false, error: null, wallets: null, seq: this.source.seq };
      this.keyedExpected = null;
      if (id) this.fetchSourceWallets();
    },
    'form.dest_branch_id'(id) {
      this.rotateIdempotencyKey();
      this.dest = { loading: false, error: null, wallets: null, seq: this.dest.seq };
      if (id) this.fetchDestWallets();
    },
    'form.deactivate_source'() {
      this.rotateIdempotencyKey();
    },
    selectedFile() {
      this.rotateIdempotencyKey();
    },
  },
  methods: {
    rotateIdempotencyKey() {
      // Never rotate while a submit is in flight (the in-flight request's retry-dedup key must stay).
      if (this.isSubmitting) return;
      this.idempotencyKey = `migrate-${crypto.randomUUID()}`;
      this.showConfirm = false;
    },
    isZero: isZeroAmount,
    isHeld(w) {
      return !!w && Number(w.reserved_amount) > 0;
    },
    sourceWallet(type) {
      const w = this.source.wallets || {};
      return (type === 'FREIGHT' ? w.freight : w.cod) || {};
    },
    destWallet(type) {
      const w = this.dest.wallets || {};
      return (type === 'FREIGHT' ? w.freight : w.cod) || {};
    },
    resultFor(type) {
      if (!this.result) return null;
      return type === 'FREIGHT' ? this.result.freight : this.result.cod;
    },
    formatMoney(v) {
      if (v === null || v === undefined || v === '') return '-';
      return `${moneyFormatter.format(Number(v))} ກີບ`;
    },
    positionLabel(balance) {
      const b = Number(balance) || 0;
      if (isZeroAmount(b)) return 'ວ່າງ (0.00)';
      return b > 0 ? 'ມີເງິນ' : 'ຕິດໜີ້';
    },
    positionClass(balance) {
      const b = Number(balance) || 0;
      if (isZeroAmount(b)) return 'is-light';
      return b > 0 ? 'is-success is-light' : 'is-danger is-light';
    },
    branchText(branch, id) {
      if (branch) return `${branch.prefix ? branch.prefix + ' - ' : ''}${branch.name || ''}`;
      return `#${id}`;
    },
    async fetchWallets(branchId) {
      const response = await walletService.getWallets(this.$axios, branchId);
      const data = response?.data || {};
      if (!data.freight_wallet || !data.cod_wallet) {
        throw new Error('ສາຂານີ້ຍັງບໍ່ມີກະເປົ໋າ');
      }
      return { freight: data.freight_wallet, cod: data.cod_wallet };
    },
    async fetchSourceWallets() {
      const id = this.form.source_branch_id;
      if (!id) return;
      const seq = ++this.source.seq;
      this.source.loading = true;
      this.source.error = null;
      try {
        const wallets = await this.fetchWallets(id);
        if (seq !== this.source.seq) return; // a newer fetch superseded this one
        // A changed balance is a changed INTENT (new expected values) → new key. An identical
        // re-read keeps the key so a resubmit after an ambiguous error is still deduped. Compared
        // against the pair the CURRENT key was minted for, not the last wallets object — a failed
        // re-read in between (wallets null) must not hide a move.
        const expected = `${toExpected(wallets.freight.balance)}|${toExpected(wallets.cod.balance)}`;
        const changed = this.keyedExpected !== null && this.keyedExpected !== expected;
        this.source.wallets = wallets;
        if (changed) this.rotateIdempotencyKey();
        this.keyedExpected = expected;
      } catch (error) {
        if (seq !== this.source.seq) return;
        const { message } = extractErrorInfo(error);
        this.source.error = message || 'ບໍ່ສາມາດໂຫລດຍອດກະເປົ໋າໄດ້';
        this.source.wallets = null;
      } finally {
        if (seq === this.source.seq) this.source.loading = false;
      }
    },
    async fetchDestWallets() {
      const id = this.form.dest_branch_id;
      if (!id) return;
      const seq = ++this.dest.seq;
      this.dest.loading = true;
      this.dest.error = null;
      try {
        const wallets = await this.fetchWallets(id);
        if (seq !== this.dest.seq) return;
        this.dest.wallets = wallets;
      } catch (error) {
        if (seq !== this.dest.seq) return;
        const { message } = extractErrorInfo(error);
        this.dest.error = message || 'ບໍ່ສາມາດໂຫລດຍອດກະເປົ໋າໄດ້';
        this.dest.wallets = null;
      } finally {
        if (seq === this.dest.seq) this.dest.loading = false;
      }
    },
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, 'ປະເພດໄຟລ໌ບໍ່ຖືກຕ້ອງ (ຮອງຮັບ: JPG, PNG, GIF, PDF)');
        event.target.value = '';
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, 'ໄຟລ໌ໃຫຍ່ເກີນໄປ (ສູງສຸດ 5MB)');
        return;
      }
      this.selectedFile = file;
    },
    clearFile() {
      this.selectedFile = null;
      this.$refs.fileInput.value = '';
    },
    close() {
      // prevent-close blocks overlay/ESC only; the header X still calls this. Mid-submit the money
      // call is in flight — keep the dialog so the result (and the list update) is not lost.
      if (this.isSubmitting) {
        this.isOpen = true;
        return;
      }
      this.isOpen = false;
      this.$emit('close');
    },
    // After the result panel: hand the outcome to the list so both branches refresh, then close.
    finish() {
      const r = this.result;
      this.$emit('success', {
        sourceBranchId: r.source_branch_id,
        destBranchId: r.dest_branch_id,
        freight: r.freight,
        cod: r.cod,
        sourceDeactivated: r.source_deactivated,
        finalized: r.finalized,
        isDuplicate: r.is_duplicate,
      });
      this.close();
    },
    requestConfirm() {
      if (!this.canSubmit) return;
      this.submitError = null;
      this.submitErrorRequestId = null;
      this.showConfirm = true;
    },
    async submit() {
      // The money call is unreachable without the explicit confirmation stage.
      if (!this.showConfirm) return;
      if (!this.canSubmit) return;

      this.isSubmitting = true;
      this.submitError = null;
      this.submitErrorRequestId = null;

      try {
        const w = this.source.wallets;
        const res = await walletService.migrateBranch(this.$axios, {
          sourceBranchId: this.form.source_branch_id,
          destBranchId: this.form.dest_branch_id,
          // Exactly the values on screen — Go refuses (409) if the live balance moved since.
          expectedFreightBalance: toExpected(w.freight.balance),
          expectedCodBalance: toExpected(w.cod.balance),
          deactivateSource: this.form.deactivate_source,
          remark: this.form.remark,
          slipImage: this.selectedFile,
          idempotencyKey: this.idempotencyKey,
        });

        if (res?.success) {
          // is_duplicate lives under res.data (same envelope as transfer).
          const data = res?.data || res;
          if (data?.is_duplicate) {
            this.$notification.OpenNotification_Error('top-right', 'warning', 5000, 'ລາຍການນີ້ຖືກດຳເນີນການແລ້ວ');
          } else {
            this.$notification.OpenNotification_AddItem_OnSuccess('top-right', 'dark', 3000);
          }
          this.isSubmitting = false;
          this.showConfirm = false;
          this.result = data;
        } else {
          throw new Error(res?.message || 'ບໍ່ສາມາດຍ້າຍກະເປົ໋າໄດ້');
        }
      } catch (error) {
        console.error('Migrate error:', error.response?.data || error);
        const { message, requestId } = extractErrorInfo(error);
        const status = error.response?.status;
        const details = error.response?.data?.error?.details || {};
        let msg = message || 'ບໍ່ສາມາດຍ້າຍກະເປົ໋າໄດ້';
        // A guard 409 names the wallet type and carries the live value: say which and how much, then
        // re-read the source so the displayed (= expected) values are current again.
        if (status === 409 && details.wallet_type) {
          const typeLabel = (WALLET_TYPES.find(t => t.value === details.wallet_type) || {}).label || details.wallet_type;
          if (details.live_balance !== undefined) {
            msg = `${typeLabel}: ຍອດປ່ຽນແລ້ວ — ຍອດປັດຈຸບັນ ${this.formatMoney(details.live_balance)}. ກວດສອບຍອດໃໝ່ແລ້ວລອງອີກຄັ້ງ.`;
          } else if (details.reserved_amount !== undefined) {
            msg = `${typeLabel}: ມີຍອດຖືກຈອງ ${this.formatMoney(details.reserved_amount)} — ລໍຖ້າໃຫ້ໝົດອາຍຸແລ້ວລອງໃໝ່.`;
          }
        }
        this.submitError = msg;
        this.submitErrorRequestId = requestId;
        this.isSubmitting = false;
        // Back to the form so the error is visible. The SAME idempotencyKey is retained unless the
        // re-read shows a moved balance (then the intent — the expected values — really changed).
        this.showConfirm = false;
        if (status === 409) this.fetchSourceWallets();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.dialog-title {
  font-size: 18px;
  font-weight: 600;
  i { margin-right: 8px; color: #d9534f; }
}
.dialog-content { padding: 12px 20px; }
// vuesax's dialog has no scroll mode: a tall form (two live-wallet panels + the confirm panel) would
// push the footer below the fold on a laptop viewport. Scroll the BODY inside the dialog and keep
// the header/footer fixed. ::v-deep because the wrapper elements belong to vs-dialog, not this
// component's scope.
::v-deep .vs-dialog__content { max-height: calc(100vh - 230px); overflow-y: auto; }
.migrate-warning { padding: 10px 14px; margin-bottom: 14px; font-size: 13px; }
.form-group {
  margin-bottom: 14px;
  .label { display: block; margin-bottom: 6px; font-weight: 500; color: #333; }
  .required { color: #dc3545; }
  .textarea { width: 100%; }
  .help-text { font-size: 12px; color: #666; margin-top: 4px; }
  .help-error { color: #dc3545; }
}
.help-text { font-size: 12px; color: #666; margin-top: 4px; }
.help-error { color: #dc3545; }
.wallet-panel {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 14px;
  .panel-heading-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
  .panel-title { font-weight: 600; color: #333; font-size: 13px; }
  .refresh-btn { height: 26px; }
  .wallet-table { background: transparent; margin-bottom: 4px; font-size: 13px; }
}
.deactivate-check { font-weight: 500; color: #333; input { margin-right: 6px; } }
.file-upload {
  display: flex; align-items: center; gap: 12px;
  .file-name { color: #28a745; font-size: 14px; }
}
.migrate-confirm { margin-top: 12px; }
.submit-error { margin-top: 12px; .error-ref { font-size: 12px; color: #856404; margin-top: 4px; } }
.migrate-result {
  .result-branches { margin-top: 6px; font-weight: 600; i { margin: 0 8px; } }
  .result-table { font-size: 13px; }
}
.dialog-footer {
  display: flex; justify-content: flex-end; gap: 12px; padding: 14px 20px; border-top: 1px solid #eee;
}
// The global `.button { background: $primary-color }` beats Bulma's is-* colours: scope the
// secondary buttons back to neutral, and the finalize (close-wallets) variant red so it never
// reads as the ordinary blue action.
.button.is-light, .button.is-white { background: #f5f5f5; color: #363636; &:hover { background: #e8e8e8; } }
.button.migrate-btn.is-danger { background: #d9534f; &:hover { background: #c9302c; } }
.button.migrate-btn.is-danger[disabled] { background: #e9a5a3; }
</style>
